import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../lib/api';
import { 
  ShoppingCart, 
  MagnifyingGlass,
  Funnel,
  X
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

const Shop = () => {
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [addingToCart, setAddingToCart] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          api.get('/products'),
          api.get('/categories')
        ]);
        setProducts(productsRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        toast.error('Failed to load products');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredProducts = products.filter(product => {
    const matchesCategory = !selectedCategory || product.category === selectedCategory;
    const matchesSearch = !searchQuery || 
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleAddToCart = async (productId) => {
    if (!isAuthenticated) {
      toast.error('Please login to add items to cart');
      return;
    }

    setAddingToCart(prev => ({ ...prev, [productId]: true }));
    try {
      await addToCart(productId, 1);
      toast.success('Added to cart!');
    } catch (error) {
      toast.error(error.message || 'Failed to add to cart');
    } finally {
      setAddingToCart(prev => ({ ...prev, [productId]: false }));
    }
  };

  const getVariationKey = (v) => v.size || v.color || JSON.stringify(v);

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="shop-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-outfit text-3xl sm:text-4xl font-light text-white mb-2">
            Merch <span className="text-[#D4AF37]">Shop</span>
          </h1>
          <p className="text-zinc-400">Premium trading gear and accessories</p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-col sm:flex-row gap-4 mb-8"
        >
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <Input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#121212] border-zinc-800 text-white placeholder:text-zinc-600 focus:border-[#D4AF37]"
              data-testid="shop-search"
            />
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-2 flex-wrap">
            <Funnel className="w-5 h-5 text-zinc-500" />
            <Button
              variant={!selectedCategory ? "default" : "outline"}
              onClick={() => setSelectedCategory(null)}
              className={!selectedCategory 
                ? "bg-[#D4AF37] text-black" 
                : "border-zinc-700 text-zinc-400 hover:text-white"
              }
              size="sm"
            >
              All
            </Button>
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                onClick={() => setSelectedCategory(category)}
                className={selectedCategory === category 
                  ? "bg-[#D4AF37] text-black" 
                  : "border-zinc-700 text-zinc-400 hover:text-white"
                }
                size="sm"
              >
                {category}
              </Button>
            ))}
          </div>
        </motion.div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-[#121212] border border-white/5 animate-pulse">
                <div className="h-64 bg-zinc-800" />
                <div className="p-4">
                  <div className="h-4 bg-zinc-800 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-zinc-800 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-zinc-400 mb-4">No products found</p>
            {(searchQuery || selectedCategory) && (
              <Button 
                variant="outline" 
                onClick={() => { setSearchQuery(''); setSelectedCategory(null); }}
                className="border-zinc-700 text-white"
              >
                <X className="w-4 h-4 mr-2" />
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.product_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-[#121212] border border-white/5 overflow-hidden group card-hover"
                data-testid={`product-card-${product.product_id}`}
              >
                {/* Product Image */}
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={product.image_url || 'https://images.unsplash.com/photo-1634032188532-f11af97817ab?w=400'}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  {product.stock <= 0 && (
                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                      <span className="text-red-500 font-bold">OUT OF STOCK</span>
                    </div>
                  )}
                  {product.stock > 0 && product.stock <= 5 && (
                    <div className="absolute top-2 right-2 bg-red-500/90 text-white text-xs px-2 py-1">
                      Only {product.stock} left
                    </div>
                  )}
                </div>

                {/* Product Info */}
                <div className="p-4">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{product.category}</p>
                  <h3 className="font-outfit text-white font-medium mb-2 line-clamp-1">{product.name}</h3>
                  <p className="text-sm text-zinc-400 mb-4 line-clamp-2">{product.description}</p>
                  
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xl text-[#D4AF37]">${product.price.toFixed(2)}</span>
                    <Button
                      onClick={() => handleAddToCart(product.product_id)}
                      disabled={product.stock <= 0 || addingToCart[product.product_id]}
                      className="bg-[#D4AF37] text-black hover:bg-[#E5C158] disabled:opacity-50"
                      size="sm"
                      data-testid={`add-to-cart-${product.product_id}`}
                    >
                      {addingToCart[product.product_id] ? (
                        <span className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full" />
                      ) : (
                        <>
                          <ShoppingCart className="w-4 h-4 mr-1" weight="duotone" />
                          Add
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Variations */}
                  {product.variations && product.variations.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/5">
                      <p className="text-xs text-zinc-500 mb-2">Available sizes:</p>
                      <div className="flex gap-1 flex-wrap">
                        {product.variations.map((v) => (
                          <span key={getVariationKey(v)} className="text-xs px-2 py-1 bg-zinc-800 text-zinc-400">
                            {v.size || v.color || JSON.stringify(v)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Cart Link */}
        {isAuthenticated && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed bottom-6 right-6"
          >
            <Link to="/cart">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#E5C158] shadow-lg gold-glow px-6 py-6">
                <ShoppingCart className="w-5 h-5 mr-2" weight="duotone" />
                View Cart
              </Button>
            </Link>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Shop;
